import { create } from "zustand";
import marketdata from "../data/Services/marketdata";
import storageService from "../data/Services/storageService";
import { useIndicatorDefaultsStore } from "./indicatorDefaultsStore";
import { useSettingsStore } from "./settingsStore";
import { setWorkspaceContext, getWorkspaceContext } from "./workspaceStore";
import useRatingStore from "./ratingStore";
import logger from "../utils/logger";

const rec = {
  onError: (error) => {
    console.error("Error:", error);
  },
  onStateChange: (state) => {
    console.log("State:", state);
  },
};

/**
 * Reset the rating store ONLY when the account context changes (multitenancy, I4).
 * ratingStore is a module singleton that would otherwise carry the previous user's
 * ratingHistory/hasCheckedServer into a different account in the same tab. Same-user
 * re-login keeps hasArmed, so the modal is never shown twice to the same user.
 * @param {string} user
 * @param {string} broker
 */
export const resetRatingOnAccountChange = (user, broker) => {
  const prev = getWorkspaceContext();
  if (prev.user !== user || prev.broker !== broker) {
    useRatingStore.getState().resetRatingData();
  }
};

/**
 * Common post-authentication logic
 * Handles state initialization after successful connection
 */
const handleAuthSuccess = async (set) => {
  // Initialize storage service with marketdata credentials
  storageService.init(marketdata.getUrl(), marketdata.signalRToken);

  set({ message: "", isAuthenticated: true });

  // Treat each login as a fresh session for the Tip of the Day — clear the
  // once-per-session flag so it auto-shows once after every login.
  sessionStorage.removeItem("odin_tip_shown");

  // Set workspace context for user-scoped storage.
  const user = marketdata.user;
  const broker = marketdata.broker;
  resetRatingOnAccountChange(user, broker); // I4: reset rating store only on account change
  setWorkspaceContext(user, broker);
  console.log(`[AuthStore] Workspace context set: ${broker}/${user}`);

  // Load user data from server after successful login
  const indicatorStore = useIndicatorDefaultsStore.getState();
  const settingsStore = useSettingsStore.getState();

  console.log("[AuthStore] 📊 Starting to load user data from server...");
  const loadStart = performance.now();

  const indicatorStart = performance.now();
  await indicatorStore.loadFromServer();
  const indicatorTime = performance.now() - indicatorStart;
  console.log(
    `[AuthStore] ✅ Indicator defaults loaded in ${indicatorTime.toFixed(2)}ms`,
  );

  const settingsStart = performance.now();
  await settingsStore.loadFromServer();
  const settingsTime = performance.now() - settingsStart;
  console.log(`[AuthStore] ✅ Settings loaded in ${settingsTime.toFixed(2)}ms`);

  const totalTime = performance.now() - loadStart;
  console.log(
    `[AuthStore] 🎯 Total user data load time: ${totalTime.toFixed(2)}ms`,
  );

  // Client specs — fire-and-forget, never blocks login. localStorage dedupe
  // skips the server roundtrip when the hardware profile hasn't changed, so
  // a returning user on a stable machine costs zero network.
  collectAndReportClientSpecs().catch((err) => {
    console.warn(
      "[AuthStore] Client specs report failed (non-fatal):",
      err?.message || err,
    );
  });
};

const SPECS_HASH_STORAGE_PREFIX = "odin-sight:last-specs-hash";

// Namespace the dedupe key by (broker, userId) so a shared browser with two
// users doesn't silently skip the second user's save when both happen to hit
// the same machine profile.
function specsHashStorageKey(broker, user) {
  return `${SPECS_HASH_STORAGE_PREFIX}:${broker || "unknown"}:${user || "unknown"}`;
}

async function collectAndReportClientSpecs() {
  const { collectClientSpecs, computeSpecsHash, SPECS_SCHEMA_VERSION } =
    await import("../utils/clientSpecs");

  const broker = marketdata.broker;
  const user = marketdata.user;
  const storageKey = specsHashStorageKey(broker, user);

  const specs = collectClientSpecs();
  const specsHash = computeSpecsHash(specs);

  let lastHash = null;
  try {
    lastHash = localStorage.getItem(storageKey);
  } catch {
    /* no-op */
  }

  if (lastHash === specsHash) {
    // Hardware + schema unchanged since this user's last login — the server
    // would respond "refreshed" anyway. Skip the roundtrip entirely.
    return;
  }

  await marketdata.saveSpecs({
    schemaVersion: SPECS_SCHEMA_VERSION,
    specsHash,
    specs,
  });

  try {
    localStorage.setItem(storageKey, specsHash);
  } catch {
    /* no-op */
  }
}

/**
 * Common authentication error handler
 */
const handleAuthFailure = (
  set,
  error,
  context = "Login",
  broker = null,
  authToken = null,
) => {
  set({
    message: "Failed to connect to market data service.",
    isAuthenticated: false,
  });

  if (error) {
    console.error(`[AuthStore] ${context} failed:`, error);
  } else {
    console.error(
      `[AuthStore] ${context} failed - marketdata state: ${marketdata.state}`,
    );
  }

  // Report authentication failure to server
  const errorMessage =
    error?.message || `Authentication failed - state: ${marketdata.state}`;
  logger.auth.error(
    `${context} failed${broker ? ` for broker ${broker}` : ""}`,
    {
      error: errorMessage,
      broker: broker || "unknown",
      state: marketdata.state,
      hasAuthToken: !!authToken,
      context,
    },
  );
};

export const useAuthStore = create((set, get) => ({
  isAuthenticated: false,
  message: "",
  loginMethod: null, // 'credentials' | 'brokerAuth' | null

  getUsername: () => {
    return marketdata.user || "";
  },
  getBroker: () => {
    return marketdata.broker || "";
  },

  /**
   * Check if user logged in via broker auth token (URL parameter)
   * @returns {boolean} - True if authenticated via broker auth token
   */
  isAuthenticatedViaBrokerAuth: () => {
    return get().loginMethod === "brokerAuth";
  },

  /**
   * Login with username and password (ITEvolution employees)
   * @param {string} username - User email/login
   * @param {string} password - User password
   * @param {string} broker - Broker identifier (default: XPI)
   * @returns {Promise<boolean>} - Success status
   */
  login: async (username, password, broker = "XPI") => {
    try {
      await marketdata.startUsingCredentials(broker, username, password);
      marketdata.receivers.push(rec);

      if (marketdata.state === "online") {
        set({ loginMethod: "credentials" });
        await handleAuthSuccess(set);
        return true;
      } else {
        handleAuthFailure(set, null, "Login");
        return false;
      }
    } catch (error) {
      handleAuthFailure(set, error, "Login");
      return false;
    }
  },

  /**
   * Auto-login using broker auth token from URL (broker users)
   * Maintains 100% compatibility with HtmlChart startUsingAuth flow
   * @param {string} broker - Broker identifier (TZ, CLEAR, XPI, RICO)
   * @param {string} authToken - Authentication token from URL
   * @returns {Promise<boolean>} - Success status
   */
  loginWithBrokerAuth: async (broker, authToken) => {
    try {
      console.log(
        `[AuthStore] Starting auto-login with token for broker: ${broker}`,
      );

      await marketdata.startUsingAuth(broker, authToken);
      marketdata.receivers.push(rec);

      if (marketdata.state === "online") {
        set({ loginMethod: "brokerAuth" });
        await handleAuthSuccess(set);
        console.log(
          `[AuthStore] Auto-login successful for user: ${marketdata.user}`,
        );
        return true;
      } else {
        handleAuthFailure(set, null, "Auto-login", broker, authToken);
        return false;
      }
    } catch (error) {
      console.error(
        `[AuthStore] Auto-login failed after trying all servers:`,
        error,
      );

      // Clear all caches when all servers fail
      // This prevents cached stale data from causing repeated failures
      console.log(
        "[AuthStore] Clearing all caches after connection failure...",
      );

      // Clear auth cache
      localStorage.removeItem("force-reload-auth");
      localStorage.removeItem("force-reload-original-token");

      // Clear symbols cache (IndexedDB)
      const { clearSymbolsCache } = await import("../utils/symbolsCache");
      await clearSymbolsCache().catch((err) => {
        console.error("[AuthStore] Failed to clear symbols cache:", err);
      });

      // Reset URL manager state
      const { urlManager } = await import("../utils/urlManager");
      urlManager.reset(broker, "marketdata");

      console.log(
        "[AuthStore] All caches cleared - user should try fresh link from broker",
      );

      handleAuthFailure(set, error, "Auto-login", broker, authToken);
      return false;
    }
  },

  /**
   * Clear all authentication and connection caches
   * Use this when user needs to force a fresh connection attempt
   * @returns {Promise<void>}
   */
  clearAuthCache: async () => {
    console.log("[AuthStore] Clearing all authentication caches...");

    // Clear localStorage auth cache
    localStorage.removeItem("force-reload-auth");
    localStorage.removeItem("force-reload-original-token");

    // Clear sessionStorage
    sessionStorage.removeItem("original-auth-token");

    // Clear symbols cache (IndexedDB)
    const { clearSymbolsCache } = await import("../utils/symbolsCache");
    await clearSymbolsCache().catch((err) => {
      console.error("[AuthStore] Failed to clear symbols cache:", err);
    });

    // Reset URL manager state for all brokers
    const { urlManager } = await import("../utils/urlManager");
    ["TZ", "XPI", "CLEAR", "RICO"].forEach((broker) => {
      urlManager.reset(broker, "marketdata");
    });

    console.log("[AuthStore] All caches cleared successfully");
  },

  logout: () => {
    marketdata.stop();
    set({ isAuthenticated: false, loginMethod: null });

    // Clean up auth data from localStorage (used for F5/reload preservation)
    localStorage.removeItem("force-reload-auth");
    localStorage.removeItem("force-reload-original-token");
    console.log("[AuthStore] Cleaned up reload auth data on logout");
  },
}));
